/**
 * Copyright (c) 2025, CYBRA Corp. All rights reserved.
 */
import { Injectable } from "@angular/core";
import { ServiceService } from "./service.service";
import { PropertyPatch } from "app/models/property-patch";
import { Url_Base } from 'app/rest-constants';
import { RestService } from './rest-service';

/*
 * Constants for the "Carton Audit" Service
 */
export const CartonAudit_ServiceType_Uuid = '80d21ff8-90a2-4c08-84fd-64557ddaccb1';
const FunctionKey_GetState = "get_state";
const FunctionKey_EnterCartonId = "enter_carton_id";
const FunctionKey_ClearReads = "clear_reads";
const FunctionKey_ClearAll = "clear_all";
const FunctionKey_Process = "process";
const FunctionKey_HandleRfidReads = "handle_rfid_reads";

/**
 * Service for the Carton Audit process.
 */
@Injectable()
export class CartonAuditService {

  constructor(
    private serviceService: ServiceService,
    private mRestService: RestService
  ) { }

  /** Returns the current state of the Carton Audit Service */
  public getCurrentState(portalId: number | string): Promise<CartonAuditService_StateInfoDto> {
    return this.serviceService.executeFunctionByKey(portalId, FunctionKey_GetState, null);
  }

  /** Starts auditing a carton by ID */
  public enterCartonId(portalId: number | string, cartonId: string): Promise<CartonAuditService_StateInfoDto> {
    const functionData: PropertyPatch[] = [{ property: "carton_id", value: cartonId }];
    return this.serviceService.executeFunctionByKey(portalId, FunctionKey_EnterCartonId, functionData);
  }

  /** Clears all reads associated with the current carton */
  public clearReads(portalId: number | string): Promise<CartonAuditService_StateInfoDto> {
    return this.serviceService.executeFunctionByKey(portalId, FunctionKey_ClearReads, null);
  }

  /** Clears current carton and resets service */
  public clearAll(portalId: number | string): Promise<CartonAuditService_StateInfoDto> {
    return this.serviceService.executeFunctionByKey(portalId, FunctionKey_ClearAll, null);
  }

  /** Processes the current carton */
  public process(portalId: number | string): Promise<CartonAuditService_StateInfoDto> {
    return this.serviceService.executeFunctionByKey(portalId, FunctionKey_Process, null);
  }

  /** Handles RFID reads sent from a Reader */
  public handleRfidReads(portalId: number | string, readerReference: string, driverObjectReferences: any[]): Promise<void> {
    const serializedDriverObjects = JSON.stringify(driverObjectReferences);
    const functionData: PropertyPatch[] = [
      { property: "reader_reference", value: readerReference },
      { property: "driverobject_references", value: serializedDriverObjects }
    ];
    return this.serviceService.executeFunctionByKey(portalId, FunctionKey_HandleRfidReads, functionData);
  }

  /** Starts the Reader */
  public startReader(portalId: number | string): Promise<any> {
    return this.mRestService.post<any>(`${Url_Base}/jockey/carton-audit/${portalId}/start-reader`, "");
  }

  /** Stops the Reader */
  public stopReader(portalId: number | string): Promise<any> {
    return this.mRestService.post<any>(`${Url_Base}/jockey/carton-audit/${portalId}/stop-reader`, "");
  }
}

/** Enum representing the current state of the Carton Audit Service */
export enum CartonAuditService_StateEnum {
  EnterCartonId = 'EnterCartonId',
  CartonBeingAudited = 'CartonBeingAudited'
}

/** DTO representing a single line/item within the Carton Audit */
export interface CartonAuditPortalAttemptLineDto {
  upc: string;
  style: string;
  color: string;
  size: string;
  packer: string;
  dimension: string;
  numberExpected: number;
  numberFound: number;
}

/** DTO representing the current Carton Audit Attempt */
export interface CartonAuditPortalAttemptDto {
  cartonId: string | null;
  startTime: string | null;
  lineItems: CartonAuditPortalAttemptLineDto[] | null;
}

/** DTO representing the current state of the Carton Audit Service */
export interface CartonAuditService_StateInfoDto {
  state: CartonAuditService_StateEnum;
  auditAttempt: CartonAuditPortalAttemptDto | null;
  readerRunning: string;
}
